import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import type { AppDispatch, RootState } from '../types'
import type { MessageEngine } from '@/engine/MessageEngine/MessageEngine'
import type { IChatsCache } from '@/services/ChatsCache/ChatsCache'
import type { ILastMessagesCache } from '@/services/LastMessagesCache/LastMessagesCache'
import type { ILastMessages, IMessage } from '@/store/messages/interfaces'
import type { ChatList } from '@/store/chats/IChats'
import { captureException } from '@/services/Sentry/Sentry'
import { getAllChats } from '../chats/selectors'
import {
  clear as clearChats,
  setPrivateChats,
  setMUChats,
  addChats,
  updateChat,
  updateChats,
  deleteChats,
  _addPrivateChat,
  _addMUChat
} from '../chats/chats'
import {
  set as setMessages,
  add as addMessage,
  update as updateMessage,
  _updateById as updateById,
  updateWithBreak,
  _remove as removeMessage,
  _readMessagesInChat
} from '../messages/messages'

const DELAY_UPDATE_TIMEOUT = 1000

interface Deps {
  messageEngine: MessageEngine
  chatsCache: IChatsCache
  lastMessagesCache: ILastMessagesCache
}

// A cached preview is stale when the last message is replaced (id/timestamp) or
// when its delivery status changes (the chat list renders a status tick).
const lastMessageChanged = (prev: IMessage | undefined, next: IMessage): boolean => {
  if (!prev) {
    return true
  }
  return prev.id !== next.id
    || prev.timestamp !== next.timestamp
    || prev.status !== next.status
}

/**
 * Persists the chat list and last messages into IndexedDB caches so the next
 * cold start can render them before `/api/user/threads` resolves.
 *
 * Writes are debounced and incremental: granular chat actions touch only the
 * affected records, deletions remove them, and only bulk roster replaces
 * (`setPrivateChats`/`setMUChats`) trigger a full re-sync. The caches are wiped
 * when the chat store is cleared (logout).
 */
export const createCacheMiddlewareInstance = (
  { messageEngine, chatsCache, lastMessagesCache }: Deps
) => {
  const cacheMiddleware = createListenerMiddleware({
    onError: error => captureException(error)
  })

  const startListening = cacheMiddleware.startListening.withTypes<
    RootState,
    AppDispatch
  >()

  const dirtyJids = new Set<string>()
  const deletedJids = new Set<string>()
  let fullResync = false

  const flushChats = (state: RootState) => {
    const resync = fullResync
    const dirty = [...dirtyJids]
    const deleted = [...deletedJids]
    fullResync = false
    dirtyJids.clear()
    deletedJids.clear()

    const chats = getAllChats(state)

    if (resync) {
      const currentJids = new Set(Object.keys(chats))
      const stale = Object.keys(chatsCache.getAll()).filter(jid => !currentJids.has(jid))
      chatsCache.remove(stale)
      chatsCache.putChats(chats)
      return
    }

    const toPut: ChatList = {}
    dirty.forEach(jid => {
      const chat = chats[jid]
      if (chat) {
        toPut[jid] = chat
      }
    })
    chatsCache.putChats(toPut)
    chatsCache.remove(deleted)
    lastMessagesCache.remove(deleted)
  }

  startListening({
    matcher: isAnyOf(
      setPrivateChats,
      setMUChats,
      addChats,
      updateChat,
      updateChats,
      deleteChats,
      _addPrivateChat,
      _addMUChat
    ),
    effect: async (action, { getState, cancelActiveListeners, delay }) => {
      if (setPrivateChats.match(action) || setMUChats.match(action)) {
        fullResync = true
      } else if (addChats.match(action)) {
        Object.keys(action.payload).forEach(jid => dirtyJids.add(jid))
      } else if (updateChats.match(action)) {
        action.payload.forEach(({ chatJid }) => dirtyJids.add(chatJid))
      } else if (updateChat.match(action)) {
        dirtyJids.add(action.payload.chatJid)
      } else if (_addPrivateChat.match(action) || _addMUChat.match(action)) {
        dirtyJids.add(action.payload.$jid)
      } else if (deleteChats.match(action)) {
        const jids = Array.isArray(action.payload) ? action.payload : [action.payload]
        jids.forEach(jid => {
          deletedJids.add(jid)
          dirtyJids.delete(jid)
        })
      }

      cancelActiveListeners()
      await delay(DELAY_UPDATE_TIMEOUT)
      flushChats(getState())
    }
  })

  // Chats whose last message might have changed, and those where it might have
  // been deleted (removeMessage). Only chats that end up with no last message are
  // dropped from the cache — we never touch chats absent from these sets, so the
  // preview survives while real history is still loading.
  const messageDirtyJids = new Set<string>()
  const messageRemovedJids = new Set<string>()

  const flushMessages = () => {
    const dirty = [...messageDirtyJids]
    const removalCandidates = [...messageRemovedJids]
    messageDirtyJids.clear()
    messageRemovedJids.clear()

    const lastMessages = messageEngine.getLastMessages(dirty)
    const changed: ILastMessages = {}
    dirty.forEach(jid => {
      const message = lastMessages[jid]
      if (message && lastMessageChanged(lastMessagesCache.getMessage(jid), message)) {
        changed[jid] = message
      }
    })
    lastMessagesCache.putMessages(changed)

    const gone = removalCandidates.filter(jid => !lastMessages[jid])
    lastMessagesCache.remove(gone)
  }

  startListening({
    matcher: isAnyOf(
      setMessages,
      addMessage,
      updateMessage,
      updateById,
      updateWithBreak,
      removeMessage,
      _readMessagesInChat
    ),
    effect: async (action, { cancelActiveListeners, delay }) => {
      if (setMessages.match(action)) {
        Object.keys(action.payload.messages).forEach(jid => messageDirtyJids.add(jid))
      } else if (addMessage.match(action)) {
        messageDirtyJids.add(action.payload.jid)
      } else if (updateMessage.match(action)) {
        const updates = Array.isArray(action.payload) ? action.payload : [action.payload]
        updates.forEach(({ jid }) => messageDirtyJids.add(jid))
      } else if (updateById.match(action)) {
        messageDirtyJids.add(action.payload.chatJid)
      } else if (updateWithBreak.match(action)) {
        messageDirtyJids.add(action.payload.chatJid)
      } else if (_readMessagesInChat.match(action)) {
        messageDirtyJids.add(action.payload.chatJid)
      } else if (removeMessage.match(action)) {
        messageDirtyJids.add(action.payload.jid)
        messageRemovedJids.add(action.payload.jid)
      }

      cancelActiveListeners()
      await delay(DELAY_UPDATE_TIMEOUT)
      flushMessages()
    }
  })

  startListening({
    actionCreator: clearChats,
    effect: () => {
      chatsCache.clear()
      lastMessagesCache.clear()
    }
  })

  return cacheMiddleware
}
